import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import SortIcon from '@mui/icons-material/Sort';
import { Box, Button, ButtonGroup, CircularProgress, IconButton, InputAdornment, Menu, MenuItem, TextField, Tooltip } from '@mui/material';
import { stripHTMLTags } from 'app/utils/appHelpers';
import { agendaCardTitleColor, agendaCardTitleFontColor } from 'config';
import { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

const AgendaListActionsContext = createContext({
    actions: null,
    setActions: () => {},
    searchOpen: false,
    setSearchOpen: () => {},
    searchQuery: '',
    setSearchQuery: () => {},
});

export function agendaMatchesSearch(agenda, query) {
    const needle = (query || '').trim().toLocaleLowerCase('tr');
    if (!needle) {
        return true;
    }
    const haystack = (text) => stripHTMLTags(text || '').toLocaleLowerCase('tr');
    return haystack(agenda?.agenda).includes(needle) || haystack(agenda?.decision).includes(needle);
}

export function filterAgendaGroups(groups, query) {
    if (!(query || '').trim()) {
        return groups;
    }
    return groups
        .map(([key, agendas]) => [key, agendas.filter((agenda) => agendaMatchesSearch(agenda, query))])
        .filter(([, agendas]) => agendas.length > 0);
}

export function AgendaListActionsProvider({ children }) {
    const [actions, setActions] = useState(null);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const meetingId = useSelector((state) => state.meeting.currentMeeting?.id);

    useEffect(() => {
        setSearchOpen(false);
        setSearchQuery('');
    }, [meetingId]);

    const value = useMemo(() => ({
        actions,
        setActions,
        searchOpen,
        setSearchOpen,
        searchQuery,
        setSearchQuery,
    }), [actions, searchOpen, searchQuery]);

    return (
        <AgendaListActionsContext.Provider value={value}>
            {children}
        </AgendaListActionsContext.Provider>
    );
}

export function useAgendaListSearch() {
    const { searchQuery, searchOpen } = useContext(AgendaListActionsContext);
    return { searchQuery, searchOpen };
}

export function useRegisterAgendaListActions({ onSort, sortTooltip, onAddAgenda, onAddPending, sorting = false, sortDisabled = false }) {
    const { setActions } = useContext(AgendaListActionsContext);
    const refs = useRef({ onSort, sortTooltip, onAddAgenda, onAddPending });
    refs.current = { onSort, sortTooltip, onAddAgenda, onAddPending };

    useEffect(() => {
        setActions({
            onSort: () => refs.current.onSort?.(),
            sortTooltip: refs.current.sortTooltip,
            onAddAgenda: () => refs.current.onAddAgenda?.(),
            onAddPending: () => refs.current.onAddPending?.(),
            sorting: Boolean(sorting),
            sortDisabled: Boolean(sortDisabled) || Boolean(sorting),
        });
    }, [setActions, sorting, sortDisabled]);

    useEffect(() => {
        return () => setActions(null);
    }, [setActions]);
}

export function AgendaSortGlyph({ spinning, fontSize = 20 }) {
    if (spinning) {
        return <CircularProgress size={fontSize - 2} color="inherit" thickness={5} />;
    }
    return <SortIcon sx={{ fontSize }} />;
}

export function AgendaListActionsBar() {
    const { actions, searchOpen, setSearchOpen, searchQuery, setSearchQuery } = useContext(AgendaListActionsContext);
    const [menuEl, setMenuEl] = useState(null);
    const inputRef = useRef(null);

    const closeMenu = () => setMenuEl(null);

    const handlePending = () => {
        closeMenu();
        actions?.onAddPending?.();
    };

    const toggleSearch = () => {
        setSearchOpen((open) => {
            if (open) {
                setSearchQuery('');
            }
            return !open;
        });
    };

    useEffect(() => {
        if (searchOpen) {
            const timer = setTimeout(() => inputRef.current?.focus(), 250);
            return () => clearTimeout(timer);
        }
    }, [searchOpen]);

    if (!actions) {
        return null;
    }

    return (
        <Box
            sx={{
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                position: 'relative',
                zIndex: 2,
                ml: 1,
            }}
        >
            <Box
                sx={{
                    position: 'absolute',
                    right: '100%',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: searchOpen ? 300 : 0,
                    overflow: 'hidden',
                    transition: 'width 0.25s ease',
                    bgcolor: agendaCardTitleColor,
                    pr: searchOpen ? 0.5 : 0,
                    pointerEvents: searchOpen ? 'auto' : 'none',
                }}
            >
                <TextField
                    inputRef={inputRef}
                    size="small"
                    fullWidth
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder="Gündem veya karar içinde ara..."
                    InputProps={{
                        endAdornment: searchQuery ? (
                            <InputAdornment position="end">
                                <IconButton
                                    size="small"
                                    aria-label="Aramayı temizle"
                                    onClick={() => setSearchQuery('')}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </InputAdornment>
                        ) : null,
                    }}
                    sx={{
                        minWidth: 280,
                        backgroundColor: '#fff',
                        borderRadius: 1,
                        '& .MuiInputBase-root': { height: 32 },
                    }}
                />
            </Box>
            <Tooltip title={searchOpen ? 'Aramayı kapat' : 'Gündem veya kararda ara'} arrow placement="top">
                <IconButton
                    size="small"
                    onClick={toggleSearch}
                    sx={{ color: searchOpen ? 'primary.main' : agendaCardTitleFontColor, p: 0.5 }}
                    aria-label={searchOpen ? 'Aramayı kapat' : 'Gündem veya kararda ara'}
                >
                    <SearchIcon />
                </IconButton>
            </Tooltip>
            <Tooltip title={actions.sorting ? 'Sıralama yapılıyor' : actions.sortTooltip} arrow placement="top">
                <span>
                    <IconButton
                        size="small"
                        onClick={actions.onSort}
                        disabled={actions.sortDisabled}
                        sx={{ color: agendaCardTitleFontColor, p: 0.5 }}
                        aria-label={actions.sorting ? 'Sıralama yapılıyor' : actions.sortTooltip}
                    >
                        <AgendaSortGlyph spinning={actions.sorting} fontSize={20} />
                    </IconButton>
                </span>
            </Tooltip>
            <ButtonGroup variant="contained" size="small">
                <Button
                    startIcon={<AddIcon />}
                    onClick={actions.onAddAgenda}
                    title="Yeni gündem ekle"
                    sx={{
                        textTransform: 'none',
                        px: 1.25,
                        height: 28,
                        fontWeight: 600,
                        '& .MuiButton-startIcon': { mr: 0.5 },
                    }}
                >
                    Ekle
                </Button>
                <Button
                    size="small"
                    aria-label="Askıdaki gündemlerden ekle"
                    title="Askıdaki gündemlerden ekle"
                    onClick={(event) => setMenuEl(event.currentTarget)}
                    sx={{ minWidth: 28, width: 28, px: 0, height: 28 }}
                >
                    <ArrowDropDownIcon />
                </Button>
            </ButtonGroup>
            <Menu
                anchorEl={menuEl}
                open={Boolean(menuEl)}
                onClose={closeMenu}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                <MenuItem onClick={handlePending}>Askıdaki gündemlerden</MenuItem>
            </Menu>
        </Box>
    );
}
